"use client";

import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { ArrowUp, MessagesSquare } from "lucide-react";
import { cn } from "@/app/_module/utils/utils";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { Button } from "@/app/_module/components/ui/button";
import { forumService, FeedSort, formatScore, forumPath } from "./ForumService";
import { CategoryIconTile } from "./categoryIcons";
import { CreateSubDialog } from "./CreateSubDialog";

// ── Sort tabs (Hot / New / Top) ───────────────────────────────────────────────
const SORTS: { key: FeedSort; label: string }[] = [
  { key: "hot", label: "Hot" },
  { key: "new", label: "New" },
  { key: "top", label: "Top" },
];

export function SortTabs({ sort, onChange }: { sort: FeedSort; onChange: (s: FeedSort) => void }) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border bg-muted/50 p-0.5" role="tablist" aria-label="Sort threads">
      {SORTS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={sort === key}
          onClick={() => onChange(key)}
          className={cn(
            "rounded px-2.5 py-1 text-[12px] font-semibold transition-colors",
            sort === key ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ── Category rail — a list of rooms ──────────────────────────────────────────
export function CategoryRail({ activeSlug }: { activeSlug?: string | null }) {
  const router = useRouter();
  const { data: categories, isLoading } = useQuery({
    queryKey: ["forum", "categories"],
    queryFn: forumService.getCategories,
    staleTime: 10 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-7 w-full rounded" />
        ))}
      </div>
    );
  }

  const item = (active: boolean) =>
    cn(
      "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px] transition-colors",
      active ? "bg-primary/[0.07] font-semibold text-primary" : "text-foreground/80 hover:bg-muted hover:text-foreground",
    );

  return (
    <nav className="space-y-0.5" aria-label="Categories">
      <button type="button" onClick={() => router.push(forumPath())} className={item(!activeSlug)} aria-current={!activeSlug ? "page" : undefined}>
        <span className={cn("inline-flex h-5 w-5 items-center justify-center", !activeSlug ? "text-primary" : "text-muted-foreground")}>
          <MessagesSquare className="h-4 w-4" strokeWidth={2} />
        </span>
        All threads
      </button>
      {(categories || []).map((c) => {
        const active = activeSlug === c.slug;
        return (
          <button
            key={c.id}
            type="button"
            onClick={() => router.push(forumPath(`/c/${c.slug}`))}
            className={item(active)}
            aria-current={active ? "page" : undefined}
          >
            <CategoryIconTile slug={c.slug} active={active} />
            <span className="flex-1 truncate">{c.name}</span>
            {c.postCount > 0 && (
              <span className={cn("font-mono text-[11px] tabular-nums", active ? "text-primary" : "text-muted-foreground/70")}>
                {formatScore(c.postCount)}
              </span>
            )}
          </button>
        );
      })}
      <CreateSubDialog />
    </nav>
  );
}

// Mobile: horizontal chip scroller under the sort tabs.
export function CategoryChips({ activeSlug }: { activeSlug?: string | null }) {
  const router = useRouter();
  const { data: categories } = useQuery({
    queryKey: ["forum", "categories"],
    queryFn: forumService.getCategories,
    staleTime: 10 * 60_000,
  });
  if (!categories?.length) return null;
  const chip = (active: boolean) =>
    cn(
      "h-7 shrink-0 rounded-md border px-2.5 text-[12px] font-medium transition-colors",
      active ? "border-primary bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:text-foreground",
    );
  return (
    <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-2 lg:hidden [scrollbar-width:none]">
      <button type="button" className={chip(!activeSlug)} onClick={() => router.push(forumPath())}>
        All
      </button>
      {categories.map((c) => (
        <button key={c.id} type="button" className={chip(activeSlug === c.slug)} onClick={() => router.push(forumPath(`/c/${c.slug}`))}>
          {c.name}
        </button>
      ))}
    </div>
  );
}

// ── Skeletons + empty state ─────────────────────────────────────────────────
export function PostFeedSkeleton() {
  return (
    <div className="divide-y rounded-lg border bg-card">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex gap-3 px-4 py-3">
          <Skeleton className="h-14 w-8 rounded" />
          <div className="flex-1 space-y-2 py-1">
            <Skeleton className="h-3 w-32 rounded" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ForumEmptyState({ title, subtitle, action }: { title: string; subtitle?: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed bg-card px-6 py-14 text-center">
      <MessagesSquare className="h-6 w-6 text-muted-foreground/60" strokeWidth={1.75} />
      <p className="forum-serif mt-3 text-[18px] text-foreground">{title}</p>
      {subtitle && <p className="mt-1 max-w-sm text-[13px] leading-5 text-muted-foreground">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── "New posts" pill — appears when the 60s latest-poll beats the feed ──────
export function NewPostsPill({ newestLoaded, onRefresh }: { newestLoaded?: string | null; onRefresh: () => void }) {
  const { data } = useQuery({
    queryKey: ["forum", "latest"],
    queryFn: forumService.getLatest,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const hasNew = !!data?.latest && !!newestLoaded && new Date(data.latest) > new Date(newestLoaded);
  if (!hasNew) return null;
  return (
    <div className="sticky top-2 z-10 flex justify-center pb-2">
      <Button size="sm" variant="secondary" className="h-7 gap-1 rounded-md border text-xs shadow-sm" onClick={onRefresh}>
        <ArrowUp className="h-3 w-3" />
        New threads
      </Button>
    </div>
  );
}
